import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowRight, Fuel, AlertTriangle, Package, MapPin, Radio, CheckCircle, Phone } from 'lucide-react';

const servicesData = {
  akaryakit: {
    icon: Fuel,
    title: 'Akaryakıt Taşımacılığı',
    subtitle: 'Benzin, Motorin ve Petrol Ürünleri',
    description: 'Kontrollü yükleme, güvenli taşıma, zamanında teslimat. Türkiye genelinde akaryakıt operasyonları.',
    image: '/images/svc-akaryakit.webp',
    forWho: ['Akaryakıt dağıtım şirketleri', 'Benzin istasyonu zincirleri', 'Sanayi kuruluşları'],
    howItWorks: ['Güzergah planlaması ve araç ataması', 'Yükleme noktasında güvenlik kontrolü', 'Gerçek zamanlı GPS takibi ile taşıma', 'Teslimat noktasında kontrollü boşaltma'],
    safety: ['ADR belgeli özel tanker araçlar', 'Sertifikalı ve deneyimli sürücüler', 'Anlık sıcaklık ve basınç izleme'],
  },
  adr: {
    icon: AlertTriangle,
    title: 'ADR Tehlikeli Madde Taşımacılığı',
    subtitle: 'Kimyasal ve Tehlikeli Madde Lojistiği',
    description: 'Uluslararası ADR standartlarına uygun, sertifikalı ekipman ve eğitimli kadroyla tehlikeli madde operasyonları.',
    image: '/images/svc-adr.webp',
    forWho: ['Kimya sanayi firmaları', 'İlaç üreticileri', 'Enerji ve madencilik şirketleri'],
    howItWorks: ['Madde sınıflandırması ve risk analizi', 'ADR belgeli araç ve ekipman hazırlığı', 'Sürekli izleme ile güvenli taşıma', 'Kontrollü boşaltma ve dokümantasyon'],
    safety: ['ADR 1-9 sınıf tüm kategorilerde yetki', 'Tehlikeli madde güvenlik danışmanlığı', 'Düzenli sürücü eğitim programları'],
  },
  lojistik: {
    icon: Package,
    title: 'Lojistik Operasyonlar',
    subtitle: 'Entegre Lojistik Çözümler',
    description: 'Depolama, elleçleme ve dağıtım dahil uçtan uca operasyon yönetimi.',
    image: '/images/svc-lojistik.webp',
    forWho: ['Üretim şirketleri', 'Perakende zincirleri', 'Dağıtım şirketleri'],
    howItWorks: ['İhtiyaç analizi ve operasyon planlaması', 'Depolama ve envanter yönetimi', 'Optimum rota planlama ve teslimat'],
    safety: ['ISO 9001 sertifikalı süreçler', 'Dijital stok takip sistemleri', '7/24 operasyon desteği'],
  },
  dagitim: {
    icon: MapPin,
    title: 'Dağıtım Planlama',
    subtitle: 'Akıllı Rota Optimizasyonu',
    description: 'Rota optimizasyonu ile zaman ve maliyet tasarrufu sağlayan dağıtım planlama.',
    image: '/images/svc-dagitim.webp',
    forWho: ['Çok noktalı dağıtım yapan firmalar', 'FMCG şirketleri', 'Gıda ve içecek dağıtıcıları'],
    howItWorks: ['Teslimat noktaları ve zaman pencereleri belirlenir', 'Optimal rotalar hesaplanır', 'Gerçek zamanlı trafik verisi ile güncelleme'],
    safety: ['Akıllı rota optimizasyon yazılımı', 'Gerçek zamanlı trafik entegrasyonu', 'Karbon ayak izi azaltma'],
  },
  'filo-takip': {
    icon: Radio,
    title: 'Filo Takip Sistemleri',
    subtitle: 'Dijital Filo Yönetimi',
    description: 'GPS tabanlı araç takip, sürücü performans izleme ve dijital operasyon yönetimi.',
    image: '/images/svc-filo-takip.webp',
    forWho: ['Filo operatörleri', 'Lojistik şirketleri', 'Kiralama şirketleri'],
    howItWorks: ['Araçlara GPS ve sensör montajı', 'Merkezi izleme platformu kurulumu', 'Gerçek zamanlı konum ve durum takibi'],
    safety: ['Saniye bazında konum takibi', 'Yakıt tüketimi izleme', 'Sürücü puanlama sistemi'],
  },
};

const ServicesPage = () => {
  const { serviceId } = useParams();
  if (!serviceId) return <ServicesOverview />;
  const service = servicesData[serviceId];
  if (!service) return <ServicesOverview />;
  return <ServiceDetail service={service} />;
};

const ServicesOverview = () => {
  return (
    <div data-testid="services-page" className="bg-white">
      <section className="page-hero-banner">
        <img src="/images/hero-slide-1.webp" alt="" className="hero-bg" loading="eager" />
        <div className="hero-gradient" />
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">Hizmetlerimiz</span>
            <h1 className="heading-xl text-white mb-4">Kontrollü ve güvenli operasyon.</h1>
            <p className="text-base text-white/70 leading-relaxed max-w-lg">Her hizmet alanımızda aynı disiplin, aynı standart.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(servicesData).map(([key, service], index) => (
              <motion.div key={key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }}>
                <Link
                  to={`/hizmetler/${key}`}
                  className="group block bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden h-full hover:shadow-[0_20px_60px_-12px_rgba(11,58,130,0.1)] hover:border-[#0B3A82]/20 transition-all duration-400"
                  data-testid={`service-link-${key}`}
                >
                  <div className="h-64 sm:h-52 overflow-hidden relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8">
                    <div className="icon-box mb-5"><service.icon className="w-5 h-5" /></div>
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 group-hover:text-[#0B3A82] transition-colors">{service.title}</h3>
                    <p className="text-sm text-[#64748B] mb-4 leading-relaxed">{service.subtitle}</p>
                    <span className="inline-flex items-center text-[#FF6B2C] text-sm font-semibold">
                      Süreci inceleyin
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-gray section-padding-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="heading-lg text-[#0B3A82] mb-5">Size özel çözümü birlikte belirleyelim.</h2>
            <p className="text-body-lg max-w-2xl mx-auto mb-10">Operasyonunuza uygun hizmeti konuşmak için ekibimize ulaşın.</p>
            <Link to="/iletisim">
              <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-10 py-6 text-base shadow-lg shadow-orange-500/20 group" data-testid="services-cta-button">
                Ekibimizle görüşün
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const ServiceDetail = ({ service }) => {
  return (
    <div data-testid="service-detail-page" className="bg-white">
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B3A82]/90 via-[#0B3A82]/70 to-[#0B3A82]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <Link to="/hizmetler" className="inline-flex items-center text-white/70 hover:text-white mb-6 text-sm font-medium transition-colors">
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Tüm Hizmetler
            </Link>
            <div className="w-14 h-14 rounded-2xl bg-[#FF6B2C] flex items-center justify-center mb-6 shadow-lg">
              <service.icon className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 tracking-tight">{service.title}</h1>
            <p className="text-lg text-white/80">{service.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="section-white section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full max-w-xl grid-cols-4 mb-14 bg-[#F1F5F9] p-1 rounded-full">
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#0B3A82] data-[state=active]:text-white rounded-full text-[#64748B] text-sm font-medium">Genel</TabsTrigger>
              <TabsTrigger value="forWho" className="data-[state=active]:bg-[#0B3A82] data-[state=active]:text-white rounded-full text-[#64748B] text-sm font-medium">Kimler İçin</TabsTrigger>
              <TabsTrigger value="howItWorks" className="data-[state=active]:bg-[#0B3A82] data-[state=active]:text-white rounded-full text-[#64748B] text-sm font-medium">Nasıl Çalışır</TabsTrigger>
              <TabsTrigger value="safety" className="data-[state=active]:bg-[#0B3A82] data-[state=active]:text-white rounded-full text-[#64748B] text-sm font-medium">Güvenlik</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
                <h2 className="text-xl font-bold text-[#0B3A82] mb-5">Hizmet Hakkında</h2>
                <p className="text-body-lg leading-relaxed">{service.description}</p>
              </motion.div>
            </TabsContent>

            <TabsContent value="forWho">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="text-xl font-bold text-[#0B3A82] mb-5">Bu Hizmet Kimler İçin?</h2>
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                  {service.forWho.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                      <CheckCircle className="w-5 h-5 text-[#0B3A82] flex-shrink-0" />
                      <span className="text-[#1A1A2E] font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="howItWorks">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="text-xl font-bold text-[#0B3A82] mb-5">Nasıl Çalışır?</h2>
                <div className="space-y-4 max-w-2xl">
                  {service.howItWorks.map((step, index) => (
                    <div key={index} className="flex gap-4 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                      <div className="w-8 h-8 bg-[#0B3A82] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                        {index + 1}
                      </div>
                      <span className="text-[#1A1A2E] pt-1 font-medium">{step}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </TabsContent>

            <TabsContent value="safety">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <h2 className="text-xl font-bold text-[#0B3A82] mb-5">Güvenlik Standartları</h2>
                <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
                  {service.safety.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                      <CheckCircle className="w-5 h-5 text-[#FF6B2C] flex-shrink-0" />
                      <span className="text-[#1A1A2E] font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="section-gray section-padding-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-xl font-bold text-[#0B3A82] mb-2">{service.title} hizmeti hakkında konuşalım.</h3>
              <p className="text-[#64748B]">Size özel çözümü birlikte planlayalım.</p>
            </div>
            <div className="flex gap-4">
              <Link to="/iletisim">
                <Button className="bg-[#FF6B2C] hover:bg-[#E55A1F] text-white rounded-full font-semibold px-7 shadow-lg shadow-orange-500/20" data-testid="service-detail-cta">Ekibimizle görüşün</Button>
              </Link>
              <a href="tel:+905305450170">
                <Button variant="outline" className="border-[#E2E8F0] text-[#1A1A2E] hover:border-[#0B3A82] hover:text-[#0B3A82] rounded-full font-semibold px-7">
                  <Phone className="w-4 h-4 mr-2" />Ara
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
